import { ChevronLeft, CreditCard, Plus, Check, Smartphone, FileText } from "lucide-react";
import { Button } from "@/app/components/button";
import { Input } from "@/app/components/input";
import { CheckoutProgress } from "@/app/components/checkout-progress";
import { useState } from "react";

export interface PaymentMethod {
  id: string;
  type: "credit" | "debit" | "pix" | "boleto";
  cardNumber?: string;
  cardHolder?: string;
  expiryDate?: string;
  isDefault?: boolean;
}

interface CheckoutPaymentPageProps {
  onBack: () => void;
  paymentMethods: PaymentMethod[];
  selectedPaymentId: string | null;
  onSelectPayment: (paymentId: string) => void;
  onAddNewCard: (card: Omit<PaymentMethod, "id">) => void;
  onContinue: () => void;
}

export function CheckoutPaymentPage({
  onBack,
  paymentMethods,
  selectedPaymentId,
  onSelectPayment,
  onAddNewCard,
  onContinue,
}: CheckoutPaymentPageProps) {
  const [showAddCard, setShowAddCard] = useState(false);
  const [cardType, setCardType] = useState<"credit" | "debit">("credit");
  const [cardNumber, setCardNumber] = useState("");
  const [cardHolder, setCardHolder] = useState("");
  const [expiryDate, setExpiryDate] = useState("");
  const [cvv, setCvv] = useState("");

  const handleAddCard = () => {
    if (cardNumber && cardHolder && expiryDate && cvv) {
      onAddNewCard({
        type: cardType,
        cardNumber: cardNumber.slice(-4),
        cardHolder,
        expiryDate,
      });
      setShowAddCard(false);
      setCardNumber("");
      setCardHolder("");
      setExpiryDate("");
      setCvv("");
    }
  };

  const formatCardNumber = (value: string) => {
    const cleaned = value.replace(/\s/g, "");
    const formatted = cleaned.match(/.{1,4}/g)?.join(" ") || cleaned;
    return formatted.slice(0, 19);
  };

  const formatExpiryDate = (value: string) => {
    const cleaned = value.replace(/\D/g, "");
    if (cleaned.length >= 2) {
      return `${cleaned.slice(0, 2)}/${cleaned.slice(2, 4)}`;
    }
    return cleaned;
  };

  return (
    <div className="min-h-screen bg-background pb-8">
      {/* Header */}
      <header className="bg-primary border-b border-primary sticky top-0 z-40 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <button
              onClick={onBack}
              className="p-2 hover:bg-white/10 rounded-lg transition-colors"
            >
              <ChevronLeft className="w-6 h-6 text-white" />
            </button>
            <div>
              <h1 className="text-xl text-white">Pagamento</h1>
              <p className="text-sm text-white/80">Escolha sua forma de pagamento</p>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Progress Indicator */}
        <CheckoutProgress currentStep={3} stepLabel="Pagamento" />

        {!showAddCard ? (
          <>
            {/* Popular Payment Methods */}
            <div className="space-y-3 mb-6">
              <h3 className="text-sm font-medium text-muted-foreground mb-3">Métodos Populares</h3>

              {/* Pix Option */}
              <button
                onClick={() => onSelectPayment("pix")}
                className={`w-full p-4 border-2 rounded-lg text-left transition-all ${
                  selectedPaymentId === "pix"
                    ? "border-primary bg-primary/5"
                    : "border-border hover:border-primary/50"
                }`}
              >
                <div className="flex items-start gap-3">
                  <div className={`mt-0.5 w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                    selectedPaymentId === "pix"
                      ? "border-primary bg-primary"
                      : "border-border"
                  }`}>
                    {selectedPaymentId === "pix" && (
                      <Check className="w-3 h-3 text-white" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <div className="w-10 h-10 bg-[#32BCAD] rounded-lg flex items-center justify-center">
                        <Smartphone className="w-5 h-5 text-white" />
                      </div>
                      <div className="flex-1">
                        <span className="font-semibold block">Pix</span>
                        <span className="text-xs text-muted-foreground">Aprovação imediata</span>
                      </div>
                      <span className="text-xs bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 px-2 py-1 rounded-full font-medium">
                        Instantâneo
                      </span>
                    </div>
                  </div>
                </div>
              </button>

              {/* Boleto Option */}
              <button
                onClick={() => onSelectPayment("boleto")}
                className={`w-full p-4 border-2 rounded-lg text-left transition-all ${
                  selectedPaymentId === "boleto"
                    ? "border-primary bg-primary/5"
                    : "border-border hover:border-primary/50"
                }`}
              >
                <div className="flex items-start gap-3">
                  <div className={`mt-0.5 w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                    selectedPaymentId === "boleto"
                      ? "border-primary bg-primary"
                      : "border-border"
                  }`}>
                    {selectedPaymentId === "boleto" && (
                      <Check className="w-3 h-3 text-white" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <div className="w-10 h-10 bg-orange-500 rounded-lg flex items-center justify-center">
                        <FileText className="w-5 h-5 text-white" />
                      </div>
                      <div className="flex-1">
                        <span className="font-semibold block">Boleto Bancário</span>
                        <span className="text-xs text-muted-foreground">Vencimento em 3 dias</span>
                      </div>
                    </div>
                  </div>
                </div>
              </button>
            </div>

            {/* Divider */}
            {paymentMethods.length > 0 && (
              <div className="relative mb-6">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-border"></div>
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-background px-2 text-muted-foreground">
                    Ou cartões salvos
                  </span>
                </div>
              </div>
            )}

            {/* Add New Card Button */}
            <button
              onClick={() => setShowAddCard(true)}
              className="w-full mb-4 p-4 border-2 border-dashed border-border hover:border-primary hover:bg-primary/5 rounded-lg transition-colors flex items-center justify-center gap-2"
            >
              <Plus className="w-5 h-5 text-primary" />
              <span className="font-medium text-primary">Adicionar Novo Cartão</span>
            </button>

            {/* Saved Cards */}
            {paymentMethods.length > 0 && (
              <div className="space-y-4 mb-6">
                {paymentMethods.map((method) => (
                  <button
                    key={method.id}
                    onClick={() => onSelectPayment(method.id)}
                    className={`w-full p-4 border-2 rounded-lg text-left transition-all ${
                      selectedPaymentId === method.id
                        ? "border-primary bg-primary/5"
                        : "border-border hover:border-primary/50"
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`mt-0.5 w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                        selectedPaymentId === method.id
                          ? "border-primary bg-primary"
                          : "border-border"
                      }`}>
                        {selectedPaymentId === method.id && (
                          <Check className="w-3 h-3 text-white" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <CreditCard className="w-4 h-4 text-primary" />
                          <span className="font-semibold">
                            {method.type === "credit" ? "Crédito" : "Débito"}
                          </span>
                          {method.isDefault && (
                            <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full">
                              Padrão
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground">
                          •••• •••• •••• {method.cardNumber}
                        </p>
                        <p className="text-sm text-muted-foreground">
                          {method.cardHolder} • Validade: {method.expiryDate}
                        </p>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}

            {/* Continue Button */}
            <Button
              onClick={onContinue}
              disabled={!selectedPaymentId}
              className="w-full"
              size="lg"
            >
              {selectedPaymentId === "pix" || selectedPaymentId === "boleto" 
                ? "Continuar para Revisão" 
                : "Continuar"
              }
            </Button>
          </>
        ) : (
          /* Add New Card Form */
          <div className="bg-card border border-border rounded-lg p-6 space-y-4">
            <h3 className="font-semibold mb-4">Adicionar Novo Cartão</h3>

            {/* Card Type */}
            <div className="flex gap-2">
              <button
                onClick={() => setCardType("credit")}
                className={`flex-1 py-3 px-4 border-2 rounded-lg font-medium transition-all ${
                  cardType === "credit"
                    ? "border-primary bg-primary/5 text-primary"
                    : "border-border"
                }`}
              >
                Crédito
              </button>
              <button
                onClick={() => setCardType("debit")}
                className={`flex-1 py-3 px-4 border-2 rounded-lg font-medium transition-all ${
                  cardType === "debit"
                    ? "border-primary bg-primary/5 text-primary"
                    : "border-border"
                }`}
              >
                Débito
              </button>
            </div>

            {/* Card Number */}
            <div>
              <label className="block text-sm font-medium mb-2">
                Número do Cartão
              </label>
              <Input
                type="text"
                placeholder="1234 5678 9012 3456"
                value={cardNumber}
                onChange={(e) => setCardNumber(formatCardNumber(e.target.value))}
                maxLength={19}
              />
            </div>

            {/* Card Holder */}
            <div>
              <label className="block text-sm font-medium mb-2">
                Nome do Titular
              </label>
              <Input
                type="text"
                placeholder="Nome como no cartão"
                value={cardHolder}
                onChange={(e) => setCardHolder(e.target.value.toUpperCase())}
              />
            </div>

            {/* Expiry and CVV */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">
                  Validade
                </label>
                <Input
                  type="text"
                  placeholder="MM/AA"
                  value={expiryDate}
                  onChange={(e) => setExpiryDate(formatExpiryDate(e.target.value))}
                  maxLength={5}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">
                  CVV
                </label>
                <Input
                  type="text"
                  placeholder="123"
                  value={cvv}
                  onChange={(e) => setCvv(e.target.value.replace(/\D/g, "").slice(0, 4))}
                  maxLength={4}
                />
              </div>
            </div>

            {/* Buttons */}
            <div className="flex gap-3 pt-4">
              <Button
                onClick={() => setShowAddCard(false)}
                variant="outline"
                className="flex-1"
              >
                Cancelar
              </Button>
              <Button
                onClick={handleAddCard}
                className="flex-1"
                disabled={!cardNumber || !cardHolder || !expiryDate || !cvv}
              >
                Adicionar Cartão
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}